import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import CalendarEventService from '../services/CalendarEventService';
import {
  CalendarEvent,
  CalendarEventListParams,
  CalendarEventRequest,
} from '../types/CalendarEvent';
import { openNotification } from '../utils';

const { TextArea } = Input;
const PAGE_SIZE = 50;

type CalendarEventFormValues = {
  name: string;
  description?: string;
  eventDate: Dayjs;
};

type CalendarEventFilterValues = {
  name?: string;
  year?: Dayjs;
};

type SortState = {
  field: 'eventDate' | 'name';
  order: 'asc' | 'desc';
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message || fallback;
  }

  return fallback;
};

const Calendar = () => {
  const [filterForm] = Form.useForm<CalendarEventFilterValues>();
  const [eventForm] = Form.useForm<CalendarEventFormValues>();
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [query, setQuery] = useState<CalendarEventListParams>({
    page: 0,
    size: PAGE_SIZE,
    sort: 'eventDate',
    direction: 'asc',
  });
  const [total, setTotal] = useState(0);

  const loadCalendarEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await CalendarEventService.getAll(query);
      setCalendarEvents(response.data.content);
      setTotal(response.data.totalElements);
    } catch (requestError) {
      const message = getErrorMessage(requestError, 'Genel Takvim kayıtları yüklenemedi.');
      setCalendarEvents([]);
      setTotal(0);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    loadCalendarEvents();
  }, [loadCalendarEvents]);

  const closeDrawer = () => {
    if (submitting) {
      return;
    }

    setDrawerOpen(false);
    setEditingEvent(null);
    eventForm.resetFields();
  };

  const openCreateDrawer = () => {
    setEditingEvent(null);
    eventForm.resetFields();
    setDrawerOpen(true);
  };

  const openEditDrawer = (calendarEvent: CalendarEvent) => {
    setEditingEvent(calendarEvent);
    eventForm.setFieldsValue({
      name: calendarEvent.name,
      description: calendarEvent.description ?? undefined,
      eventDate: dayjs(calendarEvent.eventDate),
    });
    setDrawerOpen(true);
  };

  const handleFilter = (values: CalendarEventFilterValues) => {
    setQuery((currentQuery) => ({
      ...currentQuery,
      name: values.name?.trim() || undefined,
      year: values.year?.year(),
      page: 0,
    }));
  };

  const clearFilters = () => {
    filterForm.resetFields();
    setQuery((currentQuery) => ({
      ...currentQuery,
      name: undefined,
      year: undefined,
      page: 0,
    }));
  };

  const handleSubmit = async (values: CalendarEventFormValues) => {
    if (submitting) {
      return;
    }

    const request: CalendarEventRequest = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      eventDate: values.eventDate.format('YYYY-MM-DDTHH:mm'),
    };

    setSubmitting(true);
    try {
      if (editingEvent) {
        await CalendarEventService.update(editingEvent.id, request);
        openNotification('success', 'Başarılı', 'Genel Takvim etkinliği güncellendi.');
      } else {
        await CalendarEventService.create(request);
        openNotification('success', 'Başarılı', 'Genel Takvim etkinliği eklendi.');
      }

      setDrawerOpen(false);
      setEditingEvent(null);
      eventForm.resetFields();
      await loadCalendarEvents();
    } catch (requestError) {
      openNotification(
        'error',
        'Kaydedilemedi',
        getErrorMessage(requestError, 'Genel Takvim etkinliği kaydedilemedi.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== null) {
      return;
    }

    setDeletingId(id);
    try {
      await CalendarEventService.remove(id);
      openNotification('success', 'Başarılı', 'Genel Takvim etkinliği silindi.');

      if (calendarEvents.length === 1 && (query.page ?? 0) > 0) {
        setQuery((currentQuery) => ({
          ...currentQuery,
          page: Math.max(0, (currentQuery.page ?? 0) - 1),
        }));
      } else {
        await loadCalendarEvents();
      }
    } catch (requestError) {
      openNotification(
        'error',
        'Silinemedi',
        getErrorMessage(requestError, 'Genel Takvim etkinliği silinemedi.')
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleTableChange: TableProps<CalendarEvent>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    const tableSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortField = tableSorter?.field;
    const hasSupportedSortField = sortField === 'eventDate' || sortField === 'name';
    const selectedField = hasSupportedSortField ? sortField : query.sort ?? 'eventDate';
    const selectedDirection = hasSupportedSortField
      ? (tableSorter?.order === 'descend' ? 'desc' : 'asc')
      : query.direction ?? 'asc';

    setQuery((currentQuery) => ({
      ...currentQuery,
      page: Math.max(0, (pagination.current ?? 1) - 1),
      size: pagination.pageSize ?? PAGE_SIZE,
      sort: selectedField,
      direction: selectedDirection,
    }));
  };

  const sortOrderFor = (field: SortState['field']): SortOrder => (
    query.sort === field ? (query.direction === 'desc' ? 'descend' : 'ascend') : null
  );

  const columns: TableProps<CalendarEvent>['columns'] = [
    {
      title: 'Etkinlik Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortOrderFor('name'),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (description: string | null | undefined) => description || '—',
    },
    {
      title: 'Tarih ve Saat',
      dataIndex: 'eventDate',
      key: 'eventDate',
      sorter: true,
      defaultSortOrder: 'ascend',
      sortOrder: sortOrderFor('eventDate'),
      render: (eventDate: string) => dayjs(eventDate).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_value, calendarEvent) => (
        <Space size="small">
          <Tooltip title="Genel Takvim etkinliğini düzenle">
            <Button
              aria-label={`${calendarEvent.name} kaydını düzenle`}
              icon={<EditOutlined />}
              type="text"
              onClick={() => openEditDrawer(calendarEvent)}
              disabled={deletingId !== null}
            />
          </Tooltip>
          <Tooltip title="Genel Takvim etkinliğini sil">
            <Popconfirm
              title="Genel Takvim etkinliği silinsin mi?"
              description="Bu kayıt tüm kullanıcıların takviminden kaldırılır."
              okText="Sil"
              cancelText="Vazgeç"
              okButtonProps={{ danger: true, loading: deletingId === calendarEvent.id }}
              onConfirm={() => handleDelete(calendarEvent.id)}
            >
              <Button
                aria-label={`${calendarEvent.name} kaydını sil`}
                danger
                disabled={deletingId !== null}
                icon={<DeleteOutlined />}
                type="text"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: (query.page ?? 0) + 1,
    pageSize: query.size ?? PAGE_SIZE,
    total,
    showSizeChanger: true,
    showTotal: (count) => `Toplam ${count} kayıt`,
  };

  return (
    <>
      <Card
        title="Genel Takvim"
        extra={
          <Button icon={<PlusOutlined />} type="primary" onClick={openCreateDrawer}>
            Genel Takvim Etkinliği Ekle
          </Button>
        }
      >
        <Form<CalendarEventFilterValues>
          form={filterForm}
          layout="vertical"
          onFinish={handleFilter}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={10} lg={8}>
              <Form.Item label="Ada Göre Filtrele" name="name">
                <Input allowClear placeholder="Etkinlik adı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="Yıla Göre Filtrele" name="year">
                <DatePicker className="w-full" format="YYYY" picker="year" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6} lg={10}>
              <Form.Item label=" ">
                <Space wrap>
                  <Button htmlType="submit" type="primary">
                    Filtrele
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={clearFilters}>
                    Filtreleri Temizle
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {error && (
          <Alert
            className="mb-16"
            message="Genel Takvim kayıtları yüklenemedi"
            description={error}
            showIcon
            type="error"
          />
        )}

        <Table<CalendarEvent>
          columns={columns}
          dataSource={calendarEvents}
          loading={loading}
          locale={{ emptyText: 'Genel Takvim kaydı bulunmuyor.' }}
          onChange={handleTableChange}
          pagination={pagination}
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Drawer
        destroyOnClose
        onClose={closeDrawer}
        open={drawerOpen}
        placement="right"
        title={editingEvent ? 'Genel Takvim Etkinliğini Düzenle' : 'Genel Takvim Etkinliği Ekle'}
        width={440}
      >
        <Form<CalendarEventFormValues>
          form={eventForm}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Etkinlik Adı"
            name="name"
            rules={[
              { required: true, whitespace: true, message: 'Etkinlik adı zorunludur.' },
              { max: 200, message: 'Etkinlik adı en fazla 200 karakter olabilir.' },
            ]}
          >
            <Input disabled={submitting} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            label="Açıklama"
            name="description"
            rules={[{ max: 10_000, message: 'Açıklama en fazla 10000 karakter olabilir.' }]}
          >
            <TextArea disabled={submitting} maxLength={10_000} rows={5} showCount />
          </Form.Item>
          <Form.Item
            label="Tarih ve Saat"
            name="eventDate"
            rules={[{ required: true, message: 'Tarih ve saat zorunludur.' }]}
          >
            <DatePicker
              className="w-full"
              disabled={submitting}
              format="DD.MM.YYYY HH:mm"
              showTime={{ defaultValue: dayjs('00:00', 'HH:mm'), minuteStep: 5 }}
            />
          </Form.Item>
          <Row justify="end">
            <Space>
              <Button disabled={submitting} onClick={closeDrawer}>
                Vazgeç
              </Button>
              <Button htmlType="submit" loading={submitting} type="primary">
                {editingEvent ? 'Güncelle' : 'Kaydet'}
              </Button>
            </Space>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default Calendar;
